/*
 * Voyado facet renderer — clones the <template>s in plp-voyado-filters.liquid
 * and fills [data-slot] hooks. renderFacet(facet, { onChange, onRange }) →
 * <details data-accordion-item>; exposes window.renderVoyadoFacet.
 * Gotcha: the shared cloner helpers come from voyado-card-renderer.js, which
 * loads later — read window.voyadoTmpl/voyadoSlot at call time, not load time.
 */
(function () {
  'use strict';

  // Multicolour swatch backgrounds are deferred so the image isn't fetched on every PLP load for a
  // panel that starts closed. An IntersectionObserver can't drive this: these rows sit inside the
  // translate-hidden filter drawer, the case w3c/IntersectionObserver#484 reports firing once at
  // ratio 0 and never again (see assets/drawer-scrollbar.js). The drawer's own open attribute is
  // the reliable signal, and one shared listener avoids leaking an observer per facet re-render.
  var pendingSwatchPaints = [];
  var paintListenerBound = false;

  function filtersDrawerOpen() {
    var c = document.querySelector('[data-plp-filters-drawer-open]');
    return !!c && c.getAttribute('data-plp-filters-drawer-open') === 'true';
  }

  function flushSwatchPaints() {
    while (pendingSwatchPaints.length) pendingSwatchPaints.shift()();
  }

  function paintWhenVisible(paint) {
    // Desktop renders the panel inline rather than as a drawer, so a visible swatch paints now.
    if (filtersDrawerOpen() || !document.querySelector('[data-plp-filters-drawer-open]')) { paint(); return; }
    pendingSwatchPaints.push(paint);
    if (paintListenerBound) return;
    paintListenerBound = true;
    new MutationObserver(function () {
      if (filtersDrawerOpen()) flushSwatchPaints();
    }).observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-plp-filters-drawer-open'],
    });
  }

  function tmpl() {
    return window.voyadoTmpl.apply(null, arguments);
  }
  function slot() {
    return window.voyadoSlot.apply(null, arguments);
  }

  function listTemplateName(facet) {
    if (facet.type === 'COLOR') return 'list-color';
    if (facet.type === 'SIZE') return 'list-size';
    return 'list-text';
  }
  function buildList(facet) {
    return tmpl(listTemplateName(facet)); // grid layout applied by relayoutList once the rows exist
  }

  // Size the grid to `count` items — explicit cols/rows so grid-flow-col wraps into 2 columns for long
  // lists. SIZE/COLOR/TEXT cap at 2 columns; TEXT caps rows at 6 so long lists (product type) don't break
  // into 3+ columns. Inline styles are inert on the lg flex breakpoint (list-text) — grid-only.
  function applyListLayout(list, count) {
    if (!list || !list.classList || !list.classList.contains('grid')) return;
    const cols = count > 6 ? 2 : 1;
    const rows = cols === 1 ? Math.max(count, 1) : Math.max(6, Math.ceil(count / 2));
    list.style.gridTemplateColumns = 'repeat(' + cols + ', minmax(0, 1fr))';
    list.style.gridTemplateRows = 'repeat(' + rows + ', auto)';
  }

  // Re-size the grid to the VISIBLE rows only (0-count rows are display:none via setRowVisible). Called at
  // build AND on the controller's live count refresh, so hidden rows leave no reserved tracks/gaps behind.
  function relayoutList(list) {
    if (!list) return;
    let visible = 0;
    Array.prototype.forEach.call(list.children, function (li) {
      if (li.style.display !== 'none') visible++;
    });
    applyListLayout(list, visible);
  }

  // Glue the count to the label's last word (data-count-glue → nowrap in CSS)
  function setLabelWithCount(el, label, count) {
    if (!el) return;
    el.dataset.label = label;
    const s = String(label);
    const i = s.lastIndexOf(' ');
    el.textContent = i < 0 ? '' : s.slice(0, i + 1); // head wraps freely
    const glue = document.createElement('span');
    glue.setAttribute('data-count-glue', ''); // CSS keeps last word + count together
    glue.textContent = s.slice(i + 1) + (count != null ? ' (' + count + ')' : '');
    el.appendChild(glue);
  }

  // Hide 0-count rows via inline display (li is flex); the universe keeps them in the DOM
  // so they reappear. Selected stays (to uncheck); CHECKBOX (no count) always shows.
  function setRowVisible(li, count, selected) {
    if (!li || count == null) return;
    li.style.display = count > 0 || selected ? '' : 'none';
  }

  function textRow(facetId, v, onChange) {
    const li = tmpl('row-text');
    const input = slot(li, 'input');
    input.value = v.id;
    if (v.selected) input.checked = true;
    setLabelWithCount(slot(li, 'text'), v.label, v.count);
    input.addEventListener('change', function () {
      onChange(facetId, v.id, input.checked);
    });
    setRowVisible(li, v.count, v.selected);
    return li;
  }

  function colourRow(facetId, v, onChange) {
    const li = tmpl('row-colour');
    const input = slot(li, 'input');
    input.value = v.id;
    if (v.selected) input.checked = true;
    const sw = slot(li, 'swatch');
    // Match "multicolour(ed)" / "multi coloured" / "multi-color" (any spacing/spelling), plus the MULTI id.
    const isMulti = /multicolou?r/.test(String(v.label || '').toLowerCase().replace(/[\s-]/g, '')) || String(v.id || '').toLowerCase() === 'multi';
    if (isMulti) {
      // Multicolour has no single swatch colour — show the multicolour image (URL stamped on the
      // template). This row renders inside the filter panel whether or not it's open, so painting
      // eagerly fetched the image on every PLP load; deferred to first open instead.
      const src = sw.getAttribute('data-multicolored-src');
      if (src) {
        paintWhenVisible(function () {
          sw.style.backgroundImage = 'url("' + src + '")';
          sw.style.backgroundSize = 'cover';
          sw.style.backgroundPosition = 'center';
        });
      }
    } else if (v.color) {
      sw.style.backgroundColor = v.color;
      const c = String(v.color).toLowerCase();
      if (c === '#fff' || c === '#ffffff' || c === 'white') {
        sw.classList.add('ring-1', 'ring-inset', 'ring-rule-tertiary');
      }
    }
    setLabelWithCount(slot(li, 'label'), v.label, v.count);
    input.addEventListener('change', function () {
      onChange(facetId, v.id, input.checked);
    });
    setRowVisible(li, v.count, v.selected);
    return li;
  }

  function sizeValues(facet) {
    // Flatten ALL size types + formats, not just [0] — a search can span multiple
    // size systems (Voyado returns sizeTypes[]); [0] dropped the rest. Each value
    // keeps its own id, so filtering stays correct. (Elevate's "Combine size types"
    // / size-cleaning admin settings dedupe/merge at source.)
    return (facet.sizeTypes || []).reduce(function (acc, st) {
      (st.formats || []).forEach(function (fmt) {
        if (fmt.values) acc.push.apply(acc, fmt.values);
      });
      return acc;
    }, []);
  }

  function rangeBody(facet, onRange) {
    const node = tmpl('range');
    const lo = facet.min != null ? facet.min : 0;
    const hi = facet.max != null ? facet.max : 0;
    node.setAttribute('data-range-min', lo);
    node.setAttribute('data-range-max', hi);
    const minThumb = node.querySelector('[data-plp-filters-drawer-price-thumb-min]');
    const maxThumb = node.querySelector('[data-plp-filters-drawer-price-thumb-max]');
    minThumb.min = maxThumb.min = lo;
    minThumb.max = maxThumb.max = hi;
    minThumb.value = facet.minSelected != null ? facet.minSelected : lo;
    maxThumb.value = facet.maxSelected != null ? facet.maxSelected : hi;
    // Live clamp/repaint lives in voyado-price-range.js; notify the host only on commit.
    function commit() {
      onRange(facet.id, Number(minThumb.value), Number(maxThumb.value));
    }
    minThumb.addEventListener('change', commit);
    maxThumb.addEventListener('change', commit);
    return node;
  }

  function renderFacet(facet, opts) {
    const onChange = (opts && opts.onChange) || function () {};
    const onRange = (opts && opts.onRange) || function () {};
    const acc = tmpl('accordion');
    if (!acc) return document.createComment('voyado: missing accordion template');
    acc.setAttribute('data-filter-param', facet.id);
    acc.setAttribute('data-facet-type', facet.type || '');
    slot(acc, 'title').textContent = facet.label;

    const content = slot(acc, 'content');

    if (facet.type === 'RANGE') {
      content.appendChild(rangeBody(facet, onRange));
      return acc;
    }

    let values;
    if (facet.type === 'SIZE') values = sizeValues(facet);
    else if (facet.type === 'CHECKBOX') values = [{ id: 'true', label: facet.label, selected: !!facet.selected }];
    else values = facet.values || [];

    // Voyado omits .count for 0-match options; render those as "(0)" not bare.
    if (facet.type !== 'CHECKBOX') {
      values.forEach(function (v) {
        if (v.count == null) v.count = 0;
      });
    }

    const list = buildList(facet);
    if (!list) return acc;
    const isColour = facet.type === 'COLOR';
    values.forEach(function (v) {
      list.appendChild(isColour ? colourRow(facet.id, v, onChange) : textRow(facet.id, v, onChange));
    });
    relayoutList(list); // size the grid to the visible rows (0-count rows are hidden) — no reserved space
    content.appendChild(list);
    return acc;
  }

  // Shared so the controller's live count refresh re-applies the same nowrap glue.
  window.setVoyadoLabelWithCount = setLabelWithCount;
  window.setVoyadoRowVisible = setRowVisible;
  window.setVoyadoListLayout = relayoutList; // controller calls this on live count refresh to re-collapse the grid
  window.renderVoyadoFacet = renderFacet;
})();
